import { useTranslation } from '../i18n';

export type ViewMode = 'list' | 'grid';

const STORAGE_KEY = 'emporium.catalog.viewMode';

export function readViewMode(fallback: ViewMode = 'list'): ViewMode {
    if (typeof window === 'undefined') return fallback;
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === 'grid' || v === 'list' ? v : fallback;
}

export function writeViewMode(mode: ViewMode): void {
    if (typeof window === 'undefined') return;
    try {
        window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
        // localStorage may be unavailable (private mode, quota); ignore.
    }
}

export function ViewModeToggle({
    value,
    onChange,
}: {
    value: ViewMode;
    onChange: (mode: ViewMode) => void;
}) {
    const t = useTranslation();
    return (
        <div class="inline-flex overflow-hidden rounded-md border border-surface-3 bg-surface-1">
            <ToggleButton
                active={value === 'list'}
                onClick={() => onChange('list')}
                label={t('view.list')}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
            </ToggleButton>
            <ToggleButton
                active={value === 'grid'}
                onClick={() => onChange('grid')}
                label={t('view.grid')}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
            </ToggleButton>
        </div>
    );
}

function ToggleButton({
    active,
    onClick,
    label,
    children,
}: {
    active: boolean;
    onClick: () => void;
    label: string;
    children: import('preact').ComponentChildren;
}) {
    return (
        <button
            type="button"
            title={label}
            aria-label={label}
            aria-pressed={active}
            onClick={onClick}
            class={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs transition-colors ${
                active
                    ? 'bg-accent/15 text-accent'
                    : 'text-fg-secondary hover:bg-surface-2 hover:text-fg-primary'
            }`}
        >
            {children}
        </button>
    );
}
